import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { Button } from 'primeng/button'
import { Fluid } from 'primeng/fluid'
import { InputText } from 'primeng/inputtext'
import { Tag } from 'primeng/tag'
import { combineLatest, map, of } from 'rxjs'

import { CountryCodes, MfaMethod, UserProfile, UserRole } from '@ti/contracts'
import { Profile } from '@ti/core/auth/services/my.profile'
import { isGroupRestricted } from '@ti/core/common/utils'
import { CheckIconComponent } from '@ti/core/icons/check'
import { SendIconComponent } from '@ti/core/icons/send'
import { WarningIconComponent } from '@ti/core/icons/warning'
import { XCircleIconComponent } from '@ti/core/icons/x-circle'
import { PhoneNumberInputV2Component } from '@ti/core/ui-kit/inputs'
import { MultiselectComponent } from '@ti/core/ui-kit/inputs/multiselect/multiselect.component'
import { SelectComponent } from '@ti/core/ui-kit/inputs/select/select.component'
import { ForceInvalidClassesDirective } from '@ti/core/ui-kit/layout/directives'
import { GroupsOptionsProvider } from '@ti/shared/groups/services'

import { UserFormMode, UserFormModel } from '../../models/user-form.model'
import { CredentialsFormComponent } from '../credentials-form/credentials-form.component'

@Component({
  selector: 'app-user-form',
  templateUrl: 'user-form.component.html',
  styleUrl: 'user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    Button,
    CheckIconComponent,
    CredentialsFormComponent,
    Fluid,
    ForceInvalidClassesDirective,
    InputText,
    MultiselectComponent,
    NgTemplateOutlet,
    PhoneNumberInputV2Component,
    ReactiveFormsModule,
    SelectComponent,
    SendIconComponent,
    Tag,
    TranslatePipe,
    WarningIconComponent,
    XCircleIconComponent,
  ],
})
export class UserFormComponent {
  protected readonly model = inject(UserFormModel)
  protected readonly groupOptionProvider = inject(GroupsOptionsProvider, { optional: true })
  protected readonly profile = inject(Profile)

  // A group-restricted user may only assign groups from their own group access
  protected readonly groupOptions$ = combineLatest([
    this.groupOptionProvider?.selectOptions$ ?? of([]),
    this.profile.state,
  ]).pipe(
    map(([options, profile]) => {
      const managerGroups = profile?.groupsAccess
      if (!isGroupRestricted(managerGroups)) {
        return options
      }
      return options.filter((option) => managerGroups.includes(option.value))
    }),
  )

  @Output() readonly cancelForm = new EventEmitter<void>()
  @Output() readonly submitForm = new EventEmitter<Partial<UserProfile>>()

  @Input() set mode(value: UserFormMode) {
    this.model.mode = value
  }

  @Input() set data(value: Partial<UserProfile>) {
    this.model.data = value
  }

  @Input() set country(value: CountryCodes) {
    this.model.country = value
  }

  @Input() set forceRole(value: UserRole | undefined) {
    this.model.forceRole = value
  }

  public get mode() {
    return this.model.state.mode()
  }

  public get data() {
    return this.model.state.user()
  }

  public get country() {
    return this.model.state.country()
  }

  public get forceRole() {
    return this.model.state.forceRole()
  }

  @Input() usernamePrefix?: string

  protected readonly roleOptions = computed(() => {
    const formMode = this.model.state.mode()
    return (
      (formMode === UserFormMode.TI
        ? this.model.state.adminRoleOptions()
        : formMode === UserFormMode.TSP
          ? this.model.state.tspRoleOptions()
          : this.model.state.fleetRoleOptions()) || []
    )
  })

  protected cancel() {
    this.model.cancel()
    this.cancelForm.emit()
  }

  protected onEnableUsernameAuth() {
    this.model.setAuthMethodUsernameEnabled(true)
    this.model.userForm.updateValueAndValidity()
  }

  protected onDisableUsernameAuth() {
    this.model.setAuthMethodUsernameEnabled(false)
    this.model.userForm.updateValueAndValidity()
    this.model.setUsernameError('') // clear username error
  }

  protected async onFormSubmit() {
    let isValid = true
    if (!this.model.userForm.valid) {
      isValid = false
      this.model.userForm.markAllAsTouched()
    }
    if (this.model.state.authMethodUsername() && !this.model.credentialsForm.valid) {
      isValid = false
      this.model.credentialsForm.markAllAsTouched()
    }

    if (isValid) {
      this.submitForm.emit(this.model.getData())
    }
  }

  protected readonly TYPES = { UserRole, UserFormMode, MfaMethod }
}
